import { Integer } from "model/modelTypes";
import React from "react";

/**
 * Keeps the per-box digits in sync with the single string the form holds, and
 * handles pasting a whole code at once. Shared by the legacy NumericCodeInput
 * and the redesigned CodeInput so the two cannot drift apart.
 */
function useCodeValue({
  value,
  setValue,
  length,
}: {
  value?: string;
  setValue: (val?: string) => void;
  length: Integer;
}) {
  const [valueArr, setValueArr] = React.useState<(Integer | null)[]>(
    new Array(length).fill(null)
  );

  const isInternalUpdate = React.useRef(false);

  React.useEffect(() => {
    if (value !== undefined) {
      if (isInternalUpdate.current) {
        isInternalUpdate.current = false;
        return;
      }
      setValueArr(
        new Array(length)
          .fill(null)
          .map((_, i) => (value[i] ? parseInt(value[i], 10) : null))
      );
    }
  }, [value]);

  React.useEffect(() => {
    isInternalUpdate.current = true;
    setValue(valueArr.join(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueArr]);

  const handlePaste = (event: React.ClipboardEvent) => {
    const pasteData = event.clipboardData.getData("text");
    if (/^\d+$/.test(pasteData) && pasteData.length === length) {
      setValueArr(pasteData.split("").map((char) => parseInt(char, 10)));
    }
    event.preventDefault();
  };

  return { valueArr, setValueArr, handlePaste };
}

export default useCodeValue;
